// ======================================================
// TEF Auth Hook — Login/Logout/Session Management
// ======================================================
import { useState, useEffect, useCallback } from 'react';
import { API_MODE } from '../api/config.js';

const JWT_KEY = 'tef_jwt';

/**
 * useAuth — verwaltet den Auth-State der SPA.
 *
 * Im 'local' Modus: immer eingeloggt (Entwicklung).
 * Im 'server' Modus: prüft JWT im localStorage, Login/Logout via /api/auth/*.
 */
export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Session prüfen: Lies JWT aus localStorage und dekodiere Payload
  const checkSession = useCallback(async () => {
    if (API_MODE === 'local') {
      // Lokal: immer eingeloggt als dev-User
      setUser({ username: 'dev', roles: ['admin'] });
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem(JWT_KEY);
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      // Dekodiere JWT Payload (Base64) ohne Signaturprüfung (nur clientseitig)
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          // Prüfe Ablauf
          if (payload.exp && Date.now() / 1000 > payload.exp) {
            localStorage.removeItem(JWT_KEY);
            setUser(null);
            setLoading(false);
            return;
          }
          setUser({ username: payload.id || payload.username || 'admin', roles: [payload.role || 'admin'] });
          setLoading(false);
          return;
        }
      } catch (decodeErr) {
        // Falls Decode fehlschlägt, Token entfernen
        localStorage.removeItem(JWT_KEY);
      }

      setUser(null);
    } catch (e) {
      console.warn('[Auth] Session check failed:', e.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Reagiere auf tef:auth-expired Custom Event (von StateService.js)
  useEffect(() => {
    const handleAuthExpired = () => {
      localStorage.removeItem(JWT_KEY);
      setUser(null);
    };
    window.addEventListener('tef:auth-expired', handleAuthExpired);
    return () => window.removeEventListener('tef:auth-expired', handleAuthExpired);
  }, []);

  // Login — nutzt /api/auth/login (JWT-basiert)
  const login = useCallback(async (username, password) => {
    setError(null);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await res.json();

      if (data.require2FA) {
        return { ok: true, require2FA: true, tempToken: data.tempToken };
      }

      if (data.ok && data.token) {
        localStorage.setItem(JWT_KEY, data.token);
        const u = data.user || { username };
        setUser({ username: u.username || username, roles: [u.role || 'admin'] });
        return { ok: true };
      }

      // Check lockout status
      if (data.message === 'locked') {
        const lockoutMins = data.minsLeft;
        const msg = data.permanent 
          ? 'System gesperrt wegen zu vieler Fehlversuche.' 
          : `Zu viele Fehlversuche. Bitte warten Sie ${lockoutMins} Minute(n).`;
        setError(msg);
        return { ok: false, locked: true, permanent: data.permanent, minsLeft: lockoutMins };
      }

      setError('Ungültige Anmeldedaten');
      return { ok: false, remaining: data.remaining };
    } catch (e) {
      console.error('[Auth] Login error:', e);
      setError('Verbindungsfehler bei der Anmeldung');
      return { ok: false };
    }
  }, []);

  const login2FA = useCallback(async (tempToken, code) => {
    setError(null);
    try {
      const res = await fetch('/api/auth/login/2fa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken, code })
      });
      const data = await res.json();
      
      if (data.ok && data.token) {
        localStorage.setItem(JWT_KEY, data.token);
        setUser({ username: data.user.username, roles: [data.user.role || 'admin'] });
        return { ok: true };
      }
      
      return { ok: false, message: data.message || 'Falscher Code' };
    } catch(err) {
      return { ok: false, message: 'Netzwerkfehler' };
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch (e) { /* ignore */ }
    localStorage.removeItem(JWT_KEY);
    setUser(null);
  }, []);

  return {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    login2FA,
    logout,
    checkSession,
  };
}
